import { formatWithTimeZone } from '@chronosphereio/core';
import { QueryDefinition, VariableStateMap, VariableValue } from '../model';

// This regular expression is designed to identify variable references in a template string.
// It supports 3 formats for referencing variables:
// 1. $variableName - This is a simpler format, and the regular expression captures the variable name (\w+ matches one or more word characters).
// 2. ${variableName} - Similar to the first format, but allows for more complex variable names to avoid issues with escaping characters.
//    -> also supports ${varName.field} to allow for nested fields
// 3. [COMING SOON] ${variableName:formatFn} - This is a more complex format that allows specifying a format function as well. We ignore the
// format function for now, but may support it in the future.
const TEMPLATE_VARIABLE_REGEX = /\$([a-zA-Z_]\w*)|\${([\w.]+)(?::([^}]+))?}/gm;

/**
 * Interpolates a template string with the given variable state.
 */
export function replaceTemplateVariables(text: string, variableState: VariableStateMap, timeZone?: string): string {
  const variableContextMap = convertVariableStateToVariableContextMap(variableState);
  return interpolateTemplateVariableString(text, variableContextMap, {
    timeZone,
  });
}

/**
 * Converts a variable state map to a variable context map.
 */
export function convertVariableStateToVariableContextMap(variableState: VariableStateMap): VariableContextMap {
  return Object.keys(variableState).reduce((acc, variableName) => {
    const variable = variableState[variableName];
    if (!variable) {
      return acc;
    }
    acc[variableName] = variable.value;
    return acc;
  }, {} as VariableContextMap);
}

export type VariableContextMap = Record<string, VariableValue | undefined>;

/**
 * Interpolates a template string with the given variable context.
 */
export function interpolateTemplateVariableString(
  text: string,
  variableState: VariableContextMap,
  options?: {
    formatter?: (value: VariableValue) => string;
    timeZone?: string;
  }
): string {
  const formatter = options?.formatter;
  return text.replace(TEMPLATE_VARIABLE_REGEX, (match, group1, group2, group3) => {
    let formattingFunction: undefined | string;
    let varName;
    // Matches the $varName case
    if (group1) {
      varName = group1;
    }
    // Matches the ${varName} case
    else if (group2) {
      formattingFunction = group3;
      varName = group2;
    }

    const variableValue = variableState[varName];
    if (variableValue) {
      // Use the formatter if it's provided
      if (formatter) {
        return formatter(variableValue);
      }
      return formatVariableValue(variableValue, {
        formattingFunction,
        timeZone: options?.timeZone,
      });
    } else {
      // If we can't find the variable, replace it with the original text
      return match;
    }
  });
}

// Using the same logic here as Grafana 7.5 to interpolate multi-value variables in regex queries
// https://github.com/grafana/grafana/blob/6b83895315a67979fd3b54c9160e0b400a71576c/public/app/plugins/datasource/prometheus/datasource.ts#L165-L182
// TODO: As we add more datasources, we should move this to the datasource plugin since it can be datasource specific
function prometheusSpecialRegexEscape(value: string) {
  // NB: We don't know if the value is meant to be a regex or not, so can assume if .* is present the user wants a regex so we avoid escaping it.
  // This is not ideal but it's the best we can do without more context and a larger refactor. Longer term we should consider a more robust solution.
  if (value.includes('.*')) {
    return value;
  }
  // This replaces all special regex characters with their escaped versions
  // eg /api/v1/data/events/receiver/* => /api/v1/data/events/receiver/\\*
  return value.replace(/\\/g, '\\\\\\\\').replace(/[$^*{}[\]'+?.()|]/g, '\\\\$&');
}

export function prometheusRegularEscape(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\\\'");
}

function regexFormatter(value: VariableValue) {
  const str = pipeFormatter(value);
  if (Array.isArray(value) && value.length > 1) {
    return `(${str})`;
  }
  return str;
}

function pipeFormatter(value: VariableValue) {
  if (value === null) {
    return '';
  }

  // Grafana uses a "regular" escape function when the value is a string
  // and the variable is configured to includeAll and multi. We don't have the information
  // interpolation so we just use the regular escape function for all single strings.
  // See: https://github.com/grafana/grafana/blob/6b83895315a67979fd3b54c9160e0b400a71576c/public/app/plugins/datasource/prometheus/datasource.ts#L166-L169
  if (typeof value === 'string') {
    return prometheusRegularEscape(value);
  }

  return value
    .map((v) => {
      return prometheusSpecialRegexEscape(v);
    })
    .join('|');
}

function percentencodeFormatter(value: VariableValue) {
  if (value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return encodeURIComponent(value);
  }

  return encodeURIComponent(value.join(','));
}

function rawFormatter(values: VariableValue) {
  return String(values);
}

type FormatterFn = (values: VariableValue) => string;

const formattingFunctionMap: Record<string, FormatterFn> = {
  regex: regexFormatter,
  pipe: pipeFormatter,
  percentencode: percentencodeFormatter,
  raw: rawFormatter,
};

/**
 * Formats a variable value for use in a template string.
 */
function formatVariableValue(
  templateVariableValue: VariableValue,
  options?: { formattingFunction?: string | null; timeZone?: string }
): string {
  const formattingFunction = options?.formattingFunction;
  let replaceString = '';

  if (typeof formattingFunction === 'string') {
    const formatter = formattingFunctionMap[formattingFunction];

    if (isDateFormatter(formattingFunction)) {
      replaceString = formatDate(templateVariableValue, formattingFunction, options?.timeZone);
    } else if (formatter) {
      replaceString = formatter(templateVariableValue);
    } else {
      // default to regex formatter
      replaceString = regexFormatter(templateVariableValue);
    }
  } else {
    replaceString = regexFormatter(templateVariableValue);
  }

  return replaceString;
}

export type ParseTemplateVariableArgs = {
  /**
   * If true, the formatter will be ignored and only the variable name will be returned.
   * eg. drop the :raw from ${var1:raw}
   */
  withoutFormatter?: boolean;
};

/**
 * Returns a list of template variables
 */
export const parseTemplateVariables = (text: string, args?: ParseTemplateVariableArgs) => {
  const withoutFormatter = args?.withoutFormatter ?? false;
  const regex = TEMPLATE_VARIABLE_REGEX;
  const matches = new Set<string>();
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match) {
      // Match 3 is the : format syntax
      if (match?.[3]) {
        if (match?.[1]) {
          matches.add(`${match[1]}:${match[3]}`);
        }
        // Matches the ${varName} case
        if (match?.[2]) {
          matches.add(`${match[2]}:${match[3]}`);
        }
      } else {
        // Matches the $varName case
        if (match?.[1]) {
          matches.add(match[1]);
        }
        // Matches the ${varName} case
        if (match?.[2]) {
          matches.add(match[2]);
        }
      }
    }
  }
  // return unique matches
  let values = Array.from(matches.values());

  if (withoutFormatter) {
    values = values.map((value) => value.split(':')[0] ?? '');
  }
  return values;
};

export function valueToVariableState(value: string | number) {
  return {
    value: `${value}`,
    loading: false,
  };
}

/**
 * Formats a variable value as a date.
 */
function formatDate(variableValue: VariableValue, formattingFunction: string, timeZone?: string): string {
  if (typeof variableValue !== 'string') {
    return '';
  }

  const INVALID_DATE_MSG = 'Invalid date';

  const date = new Date(Number(variableValue));
  if (isNaN(date.getTime())) {
    return INVALID_DATE_MSG;
  }

  if (formattingFunction.startsWith('date')) {
    const timeFormatter = formattingFunction.split(':')[1];
    if (timeFormatter === 'iso' || !timeFormatter) {
      return date.toISOString();
    } else if (timeFormatter === 'seconds') {
      // Milliseconds to seconds
      return Math.floor(date.getTime() / 1000).toString();
    }

    const timeFormat = formattingFunction.replace('date:', '');
    return formatWithTimeZone(date, timeFormat, timeZone);
  }

  return INVALID_DATE_MSG;
}

function isDateFormatter(formattingFunction: string) {
  return formattingFunction.startsWith('date');
}

const ESCAPED_COLON_REPLACEMENT_STRING = '__ESCAPED_COLON__';
const ESCAPED_COLON_CHARACTER = '\\:';

export function escapeColons(str: string) {
  return str.replaceAll(':', ESCAPED_COLON_CHARACTER);
}

function replaceEscapedColons(str: string) {
  return str.replaceAll(ESCAPED_COLON_CHARACTER, ESCAPED_COLON_REPLACEMENT_STRING);
}

function replaceEscapedColonStringsWithColon(str: string) {
  return str.replaceAll(ESCAPED_COLON_REPLACEMENT_STRING, ':');
}

export function stringToVariableOption(str: string) {
  const escapedString = replaceEscapedColons(str);

  const [label, value, ...others] = escapedString.split(':');

  if (label && value && others.length === 0) {
    return {
      value: replaceEscapedColonStringsWithColon(value).trim(),
      label: replaceEscapedColonStringsWithColon(label).trim(),
    };
  }

  return {
    value: replaceEscapedColonStringsWithColon(escapedString).trim(),
    label: replaceEscapedColonStringsWithColon(escapedString).trim(),
  };
}

/**
 * Returns a list of template variable names referenced in a string, stripping
 * any format specifiers (e.g. `${group_by:raw}` → `"group_by"`).
 *
 * Prefer this over `parseTemplateVariables` when you need variable names for
 * dependency tracking, cache keys, or state-map lookups — i.e. most use cases.
 */
export function parseTemplateVariableNames(text: string): string[] {
  return parseTemplateVariables(text, { withoutFormatter: true });
}

/**
 * Extracts unique template variable names from an array of query definitions.
 */
export function extractVariablesFromQueries(queries: QueryDefinition[]): string[] {
  const seen = new Set<string>();
  queries.forEach((q) => {
    const query = q.spec.plugin.spec?.query;
    if (typeof query === 'string') {
      parseTemplateVariableNames(query).forEach((v) => seen.add(v));
    }
  });
  return Array.from(seen);
}
